import { useQuery } from '@tanstack/react-query'

import type { EventPage } from '../../../services/event/fetchEventPage'

export interface UseEventExistsFromTodayArgs {
  queryKey: unknown[]
  /**
   * Fetches page one of the caller's endpoint with `resolveDecisionEventQuery`
   * merged into its own filters. Passed in rather than built here so this stays
   * endpoint-agnostic — the three callers use three different services.
   */
  fetchPage: () => Promise<EventPage>
  enabled: boolean
}

export interface UseEventExistsFromTodayReturn {
  /** `starts_at` of the earliest event from today onward; empty when none. */
  eventStartsAt: string[]
  isLoading: boolean
}

/**
 * Kept long: the answer only changes when an event is created or deleted, and
 * it decides the opening tab — a value that must not flip under the user
 * mid-session. The list queries beside it stay short-lived as before.
 */
const DECISION_STALE_TIME = 1000 * 60 * 5

const EMPTY: string[] = []

/**
 * Whether the viewer has anything from today onward — the input that decides
 * which period the schedule opens on.
 *
 * A SEPARATE query from the list, and necessarily so: the list is bounded by
 * the selected period, so asking it whether anything exists later is asking a
 * question it holds the answer out of by construction. On PAST it would report
 * no future activity and strand a schedule whose only events are next month.
 *
 * Deliberately a plain single-page fetch. `resolveDecisionEventQuery` sends
 * `per_page: 1`, so anything that followed pagination would walk the schedule
 * one row at a time to answer a question the first row already settles.
 */
export const useEventExistsFromToday = ({
  queryKey,
  fetchPage,
  enabled,
}: UseEventExistsFromTodayArgs): UseEventExistsFromTodayReturn => {
  'use memo'

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: fetchPage,
    enabled,
    staleTime: DECISION_STALE_TIME,
  })

  const starts = (data?.data ?? [])
    .map((event) => event?.starts_at)
    .filter((startsAt): startsAt is string => Boolean(startsAt))

  return {
    eventStartsAt: starts.length ? starts : EMPTY,
    // A disabled query reports `isLoading: false`; the caller is still waiting
    // on whatever gates it, and deciding the tab from an empty answer would
    // open every schedule on PAST.
    isLoading: enabled ? isLoading : true,
  }
}
